import React from 'react';

import type {RouteComponentProps} from 'react-router-dom';
import {withRouter} from 'react-router-dom';

import {ArrowLeftOutlined} from '@ant-design/icons';
import {Divider, Skeleton, Typography} from 'antd';
import styled from 'styled-components';

import {Stack} from './Stack';

import {AnchorLink} from 'components/AnchorLink';

const {Title} = Typography;
declare const _TITLE_ELE_LIST: [1, 2, 3, 4];

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  min-height: 36px;
  && > * {
    margin-bottom: 0;
  }
  .ant-skeleton {
    width: 50%;
    margin-top: 10px;
  }
  .ant-typography {
    margin: 0;
    line-height: 36px;
    display: flex;
    align-items: center;
  }
`;

const Line = styled(Divider)`
  height: 18px !important;
  top: 0 !important;
  margin: 0;
`;

interface IProps extends RouteComponentProps {
  loading?: boolean;
  title?: React.JSX.Element | string | null;
  showBack?: boolean;
  backAction?: () => void;
  backPath?: string;
  level?: (typeof _TITLE_ELE_LIST)[number];
  children?: React.ReactNode;
}

const TitleStack = styled(Stack)`
  margin-right: 20px;
`;

const ChildrenStack = styled(Stack)`
  position: relative;
  max-width: calc(100% - 52px);
  flex: 1;
`;

// eslint-disable-next-line react-refresh/only-export-components
const C: React.FC<IProps> = ({
  loading,
  title,
  children,
  history,
  backAction,
  showBack = true,
  backPath,
  match,
  level
}) => {
  const goBack = () => {
    const safeHistory = history as {
      push: (path: string) => void;
      goBack: () => void;
      length: number;
    };

    // Always prefer backPath when present, regardless of history length
    if (backPath) {
      safeHistory.push(backPath);
      return;
    }

    // If no backPath, use history.goBack() only if history has entries
    const canGoBack = safeHistory.length > 0;
    if (canGoBack) {
      safeHistory.goBack();
      return;
    }

    // Fallback: compute root path and ensure it's never empty
    const path = match.url.split('/');
    path.pop();
    const fallbackPath = path.join('/');
    // Ensure we never push an empty string, default to '/' if needed
    safeHistory.push(fallbackPath || '/');
  };

  return (
    <Header>
      <Skeleton loading={loading} paragraph={false} active>
        <TitleStack gutter={12} className="title-stack">
          {showBack && (
            <>
              <AnchorLink onClick={backAction || goBack} style={{lineHeight: '100%'}}>
                <ArrowLeftOutlined style={{fontSize: 18}} />
              </AnchorLink>{' '}
              <Line type="vertical" />
            </>
          )}
          {title && (
            <Title level={level} style={{margin: 0}}>
              {title}
            </Title>
          )}
        </TitleStack>
        <ChildrenStack direction={title ? 'row-reverse' : 'row'} gutter={8}>
          {children}
        </ChildrenStack>
      </Skeleton>
    </Header>
  );
};

export const PageHeader = withRouter(C);
